/**
 * NutritionalTargetProfilesPage — Story 6.6: Admin-Managed Nutritional Target Profiles
 *
 * Admin UI for managing the nutritional target profile corpus. Requires staff:admin role.
 * Clinical Team can create, edit, and publish profiles without an engineering deploy.
 *
 * Displays a table of profiles fetched from GET /v1/nutritional-target-profiles with:
 *   label, clinicalTrack, careplan variants, and status (draft/published).
 *
 * Provides:
 *   - Create/edit form with all bound fields + variant multi-select
 *   - Publish button (increments version, enforces coverage guard)
 *
 * @NonPhiEndpoint — clinical-track data only, no member PHI.
 * requires_compliance_reviewer: true — Clinical Team must approve bound values.
 */

import { usePortalAuth } from '@nrx/auth-ui';
import { Table } from '@nrx/ui';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';

import { SERVICES_BASE_URL } from '../../lib/services-base.js';

// ─── Types ────────────────────────────────────────────────────────────────────

interface NutritionalBoundsForm {
  carbsMinG: string;
  carbsMaxG: string;
  fiberMinG: string;
  sodiumMaxMg: string;
  proteinMinG: string;
  proteinMaxG: string;
  potassiumMaxMg: string;
  phosphorusMaxMg: string;
  satFatMaxG: string;
  cholesterolMaxMg: string;
  saturatedFatPctKcalMax: string;
  addedSugarsPctKcalMax: string;
}

interface NutritionalTargetProfileSummary {
  id: string;
  label: string;
  clinicalTrack: 'eCKM' | 'CKM';
  careplanVariants: string[];
  status: 'draft' | 'published';
  version: number;
  updatedBy: string;
  updatedAt: string;
}

const ALL_CAREPLAN_VARIANTS = ['eckm-high', 'eckm-low', 'ckm-high', 'ckm-low'] as const;

const EMPTY_BOUNDS: NutritionalBoundsForm = {
  carbsMinG: '',
  carbsMaxG: '',
  fiberMinG: '',
  sodiumMaxMg: '',
  proteinMinG: '',
  proteinMaxG: '',
  potassiumMaxMg: '',
  phosphorusMaxMg: '',
  satFatMaxG: '',
  cholesterolMaxMg: '',
  saturatedFatPctKcalMax: '',
  addedSugarsPctKcalMax: '',
};

function parseOptionalFloat(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = parseFloat(trimmed);
  return isNaN(parsed) ? null : parsed;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function NutritionalTargetProfilesPage(): JSX.Element {
  const auth = usePortalAuth();
  const [profiles, setProfiles] = useState<NutritionalTargetProfileSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Create form state
  const [label, setLabel] = useState('');
  const [indication, setIndication] = useState('');
  const [clinicalTrack, setClinicalTrack] = useState<'eCKM' | 'CKM'>('eCKM');
  const [selectedVariants, setSelectedVariants] = useState<string[]>([]);
  const [bounds, setBounds] = useState<NutritionalBoundsForm>(EMPTY_BOUNDS);
  const [rationale, setRationale] = useState('');

  const accessToken = auth.user?.access_token;

  useEffect(() => {
    void fetchProfiles();
  }, [auth]);

  async function fetchProfiles(): Promise<void> {
    if (!accessToken) {
      setError('Not authenticated — please sign in again.');
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${SERVICES_BASE_URL}/v1/core/nutritional-target-profiles`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!res.ok) {
        setError(`Failed to load profiles (${res.status})`);
        return;
      }
      const data = (await res.json()) as NutritionalTargetProfileSummary[];
      setProfiles(data);
    } catch {
      setError('Unable to connect to API.');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!accessToken) return;
    setSaving(true);
    setSaveError(null);

    const body: Record<string, unknown> = {
      label,
      indication,
      clinicalTrack,
      careplanVariants: selectedVariants,
      carbsMinG: parseFloat(bounds.carbsMinG),
      carbsMaxG: parseFloat(bounds.carbsMaxG),
      fiberMinG: parseFloat(bounds.fiberMinG),
      sodiumMaxMg: parseFloat(bounds.sodiumMaxMg),
      proteinMinG: parseFloat(bounds.proteinMinG),
      proteinMaxG: parseFloat(bounds.proteinMaxG),
    };

    // Optional bounds
    const optFields: (keyof NutritionalBoundsForm)[] = [
      'potassiumMaxMg',
      'phosphorusMaxMg',
      'satFatMaxG',
      'cholesterolMaxMg',
      'saturatedFatPctKcalMax',
      'addedSugarsPctKcalMax',
    ];
    for (const field of optFields) {
      const val = parseOptionalFloat(bounds[field]);
      if (val !== null) {
        body[field] = val;
      }
    }
    if (rationale.trim()) body['rationale'] = rationale.trim();

    try {
      const res = await fetch(`${SERVICES_BASE_URL}/v1/core/nutritional-target-profiles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = (await res.json()) as { detail?: string };
        setSaveError(`Create failed (${res.status}): ${errBody.detail ?? 'Unknown error'}`);
        return;
      }
      // Reset form and reload
      setShowCreateForm(false);
      setLabel('');
      setIndication('');
      setClinicalTrack('eCKM');
      setSelectedVariants([]);
      setBounds(EMPTY_BOUNDS);
      setRationale('');
      setLoading(true);
      await fetchProfiles();
    } catch {
      setSaveError('Unable to connect to API.');
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish(profileId: string): Promise<void> {
    if (!accessToken) return;
    setSaveError(null);
    try {
      const res = await fetch(
        `${SERVICES_BASE_URL}/v1/core/nutritional-target-profiles/${profileId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ status: 'published' }),
        },
      );
      if (!res.ok) {
        const errBody = (await res.json()) as { detail?: string };
        setSaveError(
          `Publish failed (${res.status}): ${errBody.detail ?? 'Coverage guard blocked — ensure all 4 careplan variants have a published profile.'}`,
        );
        return;
      }
      setLoading(true);
      await fetchProfiles();
    } catch {
      setSaveError('Unable to connect to API.');
    }
  }

  function toggleVariant(variant: string): void {
    setSelectedVariants((prev) =>
      prev.includes(variant) ? prev.filter((v) => v !== variant) : [...prev, variant],
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Nutritional Target Profiles</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage eCKM and CKM per-meal nutritional bounds. Published profiles drive recipe
            generation — Clinical Team sign-off required before publishing.
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {showCreateForm ? 'Cancel' : 'New Profile'}
        </button>
      </div>

      {saveError !== null && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">{saveError}</p>
        </div>
      )}

      {showCreateForm && (
        <form
          onSubmit={(e) => void handleCreate(e)}
          className="mt-6 p-6 bg-gray-50 rounded-lg border border-gray-200 space-y-4"
        >
          <h2 className="text-lg font-medium text-gray-900">Create Draft Profile</h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Label</label>
              <input
                type="text"
                required
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm"
                placeholder="e.g. eCKM Standard High-Touch"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Clinical Track</label>
              <select
                value={clinicalTrack}
                onChange={(e) => setClinicalTrack(e.target.value as 'eCKM' | 'CKM')}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm"
              >
                <option value="eCKM">eCKM</option>
                <option value="CKM">CKM</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Indication</label>
            <textarea
              required
              value={indication}
              onChange={(e) => setIndication(e.target.value)}
              rows={2}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm"
              placeholder="Clinical indication (e.g. CKD + diabetes nutritional profile)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Careplan Variants (select all this profile covers)
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              {ALL_CAREPLAN_VARIANTS.map((variant) => (
                <label key={variant} className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedVariants.includes(variant)}
                    onChange={() => toggleVariant(variant)}
                    className="rounded border-gray-300 text-indigo-600"
                  />
                  <span className="text-sm text-gray-700">{variant}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Per-Meal Bounds (required fields)
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {(
                [
                  ['carbsMinG', 'Carbs Min (g)'],
                  ['carbsMaxG', 'Carbs Max (g)'],
                  ['fiberMinG', 'Fiber Min (g)'],
                  ['sodiumMaxMg', 'Sodium Max (mg)'],
                  ['proteinMinG', 'Protein Min (g)'],
                  ['proteinMaxG', 'Protein Max (g)'],
                ] as const
              ).map(([field, label]) => (
                <div key={field}>
                  <label className="block text-xs font-medium text-gray-600">{label}</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.1"
                    value={bounds[field]}
                    onChange={(e) => setBounds((prev) => ({ ...prev, [field]: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm"
                  />
                </div>
              ))}
            </div>
            <h3 className="text-sm font-medium text-gray-700 mt-3 mb-2">
              Optional Bounds (leave blank if no constraint)
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {(
                [
                  ['potassiumMaxMg', 'Potassium Max (mg)'],
                  ['phosphorusMaxMg', 'Phosphorus Max (mg)'],
                  ['satFatMaxG', 'Sat Fat Max (g)'],
                  ['cholesterolMaxMg', 'Cholesterol Max (mg)'],
                  ['saturatedFatPctKcalMax', 'Sat Fat % kcal Max'],
                  ['addedSugarsPctKcalMax', 'Added Sugars % kcal Max'],
                ] as const
              ).map(([field, label]) => (
                <div key={field}>
                  <label className="block text-xs font-medium text-gray-600">{label}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={bounds[field]}
                    onChange={(e) => setBounds((prev) => ({ ...prev, [field]: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm"
                    placeholder="—"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Clinical Rationale (optional)
            </label>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              rows={3}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm"
              placeholder="Evidence base and clinical reasoning for these bounds…"
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || selectedVariants.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Creating…' : 'Create Draft'}
            </button>
          </div>
        </form>
      )}

      {error !== null && <p className="mt-6 text-red-600">{error}</p>}

      {error === null && (
        <div className="mt-6 overflow-x-auto">
          {!loading && profiles.length === 0 ? (
            <p className="text-gray-500">
              No profiles found. Create a draft profile to get started.
            </p>
          ) : (
            <Table.ScrollArea loading={loading} loadingLabel="Loading profiles…">
              <Table.Root className="rounded-lg border border-gray-200">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Label</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Track</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Variants</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>v</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {profiles.map((profile) => (
                    <Table.Row key={profile.id}>
                      <Table.Cell className="font-medium text-gray-900">{profile.label}</Table.Cell>
                      <Table.Cell>
                        <span className="font-mono text-xs text-gray-600">
                          {profile.clinicalTrack}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex flex-wrap gap-1">
                          {profile.careplanVariants.map((v) => (
                            <span
                              key={v}
                              className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                            >
                              {v}
                            </span>
                          ))}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <span
                          className={
                            profile.status === 'published'
                              ? 'inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800'
                              : 'inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800'
                          }
                        >
                          {profile.status}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="text-xs text-gray-500">v{profile.version}</Table.Cell>
                      <Table.Cell>
                        {profile.status === 'draft' && (
                          <button
                            onClick={() => void handlePublish(profile.id)}
                            className="inline-flex items-center rounded border border-transparent bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700 focus:outline-none"
                          >
                            Publish
                          </button>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Table.ScrollArea>
          )}
        </div>
      )}

      <div className="mt-8 p-4 bg-amber-50 rounded-lg border border-amber-200">
        <p className="text-sm text-amber-800">
          <strong>Compliance note:</strong> All bound values require Clinical Team sign-off before
          publishing (
          <code className="text-xs bg-amber-100 px-1 rounded">
            requires_compliance_reviewer: true
          </code>
          ). Do not publish profiles without explicit Clinical Team approval.
        </p>
      </div>
    </div>
  );
}
